/**
 * constants/assetClass.ts
 * Asset class taxonomy constants and display data
 *
 * Provides runtime constants for the AssetClass domain including:
 * - Enum-like constant arrays for asset class types, market segments, and sub-asset classes
 * - Display title maps for UI presentation
 * - Validation maps for hierarchical relationships
 *
 * These constants correspond to the pure type definitions in `src/types/shared/AssetClass.ts`.
 *
 * ARRAY ORDERING & STABILITY:
 * - Arrays are intentionally alphabetized for stable UI lists
 * - Consumers should rely on these arrays—not Record iteration—for display order
 * - Union duplicate values are deduped in arrays by design (stable, unique lists for UI)
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No imports from `utils/` or other domain constants
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 *
 * TESTING GUIDELINES:
 * - Do NOT import from this file in tests to assert behavior
 * - Prefer importing parametric utils and passing these maps as inputs
 * - This keeps tests decoupled from constants and focused on logic
 */

// Internal absolute imports (type-only)
import type {
  AssetClassType,
  MarketSegment,
  SubAssetClass
} from '@/types/shared/AssetClass';

/**
 * All asset class types
 * Ordered alphabetically for stable iteration
 */
export const ASSET_CLASS_TYPES = [
  'bonds',
  'cfd',
  'crypto',
  'forex',
  'funds',
  'futures',
  'options',
  'stocks'
] as const satisfies readonly AssetClassType[];

/**
 * All market segments across all asset classes
 * Ordered alphabetically for stable iteration
 */
export const MARKET_SEGMENTS = [
  'aggregate',
  'asia',
  'asia-ex-japan',
  'commodities',
  'corporate',
  'currencies',
  'digital-assets',
  'emerging-markets',
  'energies',
  'eu-equities',
  'exotics',
  'financials',
  'frontier-markets',
  'global',
  'global-equities',
  'government',
  'indices',
  'international',
  'international-equities',
  'latin-america',
  'majors',
  'meats',
  'metals',
  'minors',
  'municipal',
  'other',
  'rates',
  'regional',
  'securitized',
  'softs',
  'us-equities'
] as const satisfies readonly MarketSegment[];

/**
 * All sub-asset class types across all asset classes
 * Ordered alphabetically for stable iteration
 */
export const SUB_ASSET_CLASS_TYPES = [
  'adr',
  'aggregate',
  'ai-token',
  'all-world',
  'broad-market',
  'brazil',
  'china',
  'cross-pair',
  'crude-oil',
  'defi',
  'developed-ex-us',
  'developed-markets',
  'dividend',
  'dividend-growth',
  'em',
  'em-all-cap',
  'em-large-mid',
  'em-thematic',
  'emerging-markets',
  'energy',
  'enterprise',
  'equity-options',
  'etf',
  'etf-options',
  'europe',
  'exchange-token',
  'factor',
  'floating-rate',
  'frontier-blend',
  'futures-options',
  'g10',
  'gaming',
  'general-obligation',
  'high-yield',
  'index-options',
  'industrial',
  'infrastructure',
  'international',
  'investment-grade',
  'japan',
  'large-cap-blend',
  'large-cap-growth',
  'large-cap-value',
  'layer-0',
  'layer-1',
  'layer-2',
  'mbs',
  'meme',
  'meme-coin',
  'mexico',
  'micro',
  'mid-cap-blend',
  'mini',
  'minimum-volatility',
  'momentum',
  'mortgage-backed',
  'municipal',
  'nano',
  'natural-gas',
  'oracle',
  'other',
  'payment-token',
  'payments',
  'precious',
  'privacy',
  'quality',
  'regional-equities',
  'sector',
  'single_stock',
  'size',
  'small-cap-blend',
  'south-africa',
  'stablecoin',
  'standard',
  'stock',
  'stock-index',
  'stock_index',
  'tips',
  'total-ex-us',
  'total-market',
  'treasury',
  'value'
] as const satisfies readonly SubAssetClass[];

/**
 * Display names for asset class types
 * Immutable via 'as const' assertion
 */
export const ASSET_CLASS_TYPE_DISPLAY_MAP = {
  bonds: 'Bonds',
  cfd: 'CFDs',
  crypto: 'Crypto',
  forex: 'Forex',
  funds: 'Funds',
  futures: 'Futures',
  options: 'Options',
  stocks: 'Stocks'
} as const satisfies Readonly<Record<AssetClassType, string>>;

/**
 * Display names for market segments
 * Immutable via 'as const' assertion
 */
export const MARKET_SEGMENT_DISPLAY_MAP = {
  // Futures/Options/CFD segments
  indices: 'Indices',
  commodities: 'Commodities',
  metals: 'Metals',
  currencies: 'Currencies',
  financials: 'Financials',
  energies: 'Energies',
  meats: 'Livestock',
  softs: 'Soft Commodities',
  rates: 'Interest Rates',

  // Forex segments
  majors: 'Major Pairs',
  minors: 'Minor Pairs',
  exotics: 'Exotic Pairs',

  // Stock segments
  'us-equities': 'US Equities',
  'eu-equities': 'EU Equities',
  'emerging-markets': 'Emerging Markets',
  'international-equities': 'International Equities',
  'global-equities': 'Global Equities',
  'asia-ex-japan': 'Asia ex-Japan',
  asia: 'Asian Markets',
  'latin-america': 'Latin America',
  'frontier-markets': 'Frontier Markets',
  global: 'Global Markets',
  regional: 'Regional Markets',

  // Bond segments
  government: 'Government Bonds',
  corporate: 'Corporate Bonds',
  municipal: 'Municipal Bonds',
  securitized: 'Securitized Bonds',
  aggregate: 'Aggregate Bonds',
  international: 'International Bonds',

  // Crypto segments
  'digital-assets': 'Digital Assets',

  // Custom option
  other: 'Other'
} as const satisfies Readonly<Record<MarketSegment, string>>;

/**
 * Display names for sub-asset classes
 * Immutable via 'as const' assertion
 */
export const SUB_ASSET_CLASS_DISPLAY_MAP = {
  // Futures contract sizes
  nano: 'Nano',
  micro: 'Micro',
  mini: 'Mini',
  standard: 'Standard',

  // Forex currency categories
  g10: 'G10',
  em: 'Emerging Market',

  // Stock instrument types
  stock: 'Stock',
  etf: 'ETF',
  adr: 'ADR',

  // Options instrument types
  'equity-options': 'Equity Options',
  'etf-options': 'ETF Options',
  'index-options': 'Index Options',
  'futures-options': 'Futures Options',

  // CFD instrument types
  stock_index: 'Stock Index',
  'stock-index': 'Stock Index',
  single_stock: 'Single Stock',
  energy: 'Energy',
  'crude-oil': 'Crude Oil',
  'natural-gas': 'Natural Gas',
  precious: 'Precious Metals',
  industrial: 'Industrial Metals',

  // Crypto instrument types
  'layer-1': 'Layer 1',
  'layer-2': 'Layer 2',
  'layer-0': 'Layer 0',
  stablecoin: 'Stablecoin',
  defi: 'DeFi',
  'exchange-token': 'Exchange Token',
  meme: 'Meme',
  'meme-coin': 'Meme Coin',
  infrastructure: 'Infrastructure',
  'payment-token': 'Payment Token',
  payments: 'Payments',
  oracle: 'Oracle',
  enterprise: 'Enterprise',
  'cross-pair': 'Cross Pair',
  privacy: 'Privacy',
  'ai-token': 'AI Token',
  gaming: 'Gaming',

  // Bond instrument types
  treasury: 'Treasury',
  tips: 'TIPS',
  'investment-grade': 'Investment Grade',
  'high-yield': 'High Yield',
  municipal: 'Municipal',
  'general-obligation': 'General Obligation',
  'mortgage-backed': 'Mortgage-Backed',
  'broad-market': 'Broad Market',
  'floating-rate': 'Floating Rate',
  mbs: 'MBS',
  aggregate: 'Aggregate',
  international: 'International',
  'emerging-markets': 'Emerging Markets',

  // Fund instrument types
  'large-cap-blend': 'Large-Cap Blend',
  'large-cap-growth': 'Large-Cap Growth',
  'large-cap-value': 'Large-Cap Value',
  'mid-cap-blend': 'Mid-Cap Blend',
  'small-cap-blend': 'Small-Cap Blend',
  'total-market': 'Total Market',
  dividend: 'Dividend',
  'dividend-growth': 'Dividend Growth',
  sector: 'Sector',
  'developed-ex-us': 'Developed ex-US',
  'developed-markets': 'Developed Markets',
  'total-ex-us': 'Total ex-US',
  'all-world': 'All World',
  'em-large-mid': 'EM Large/Mid',
  'em-all-cap': 'EM All-Cap',
  'minimum-volatility': 'Minimum Volatility',
  momentum: 'Momentum',
  quality: 'Quality',
  factor: 'Factor',
  value: 'Value',
  size: 'Size',
  'regional-equities': 'Regional Equities',
  china: 'China',
  japan: 'Japan',
  europe: 'Europe',
  mexico: 'Mexico',
  brazil: 'Brazil',
  'south-africa': 'South Africa',
  'frontier-blend': 'Frontier Blend',
  'em-thematic': 'EM Thematic',

  // Custom option
  other: 'Other'
} as const satisfies Readonly<Record<SubAssetClass, string>>;

/**
 * Valid market segments mapped by asset class
 * Defines the hierarchical relationship between asset classes and their allowed market segments
 */
export const VALID_MARKET_SEGMENTS_BY_ASSET_CLASS = {
  futures: ['indices', 'commodities', 'metals', 'currencies', 'financials', 'energies', 'meats', 'softs', 'other'],
  cfd: ['indices', 'commodities', 'metals', 'us-equities', 'eu-equities', 'other'],
  forex: ['currencies', 'other'],
  options: ['indices', 'us-equities', 'metals', 'energies', 'commodities', 'currencies', 'rates', 'other'],
  stocks: ['us-equities', 'eu-equities', 'emerging-markets', 'other'],
  bonds: ['government', 'corporate', 'municipal', 'securitized', 'aggregate', 'international', 'other'],
  crypto: ['digital-assets', 'other'],
  funds: ['us-equities', 'eu-equities', 'emerging-markets', 'international-equities', 'global-equities', 'asia-ex-japan', 'asia', 'latin-america', 'frontier-markets', 'other']
} as const satisfies Readonly<Record<AssetClassType, readonly MarketSegment[]>>;

/**
 * Valid sub-asset classes mapped by asset class
 * Defines the hierarchical relationship between asset classes and their allowed sub-asset classes
 */
export const VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS = {
  futures: ['nano', 'micro', 'mini', 'standard', 'other'],
  forex: ['g10', 'em', 'other'],
  stocks: ['stock', 'etf', 'adr', 'other'],
  options: ['equity-options', 'etf-options', 'index-options', 'futures-options', 'other'],
  cfd: ['stock_index', 'stock-index', 'single_stock', 'energy', 'crude-oil', 'natural-gas', 'precious', 'industrial', 'other'],
  bonds: ['treasury', 'tips', 'investment-grade', 'high-yield', 'general-obligation', 'mortgage-backed', 'broad-market', 'developed-markets', 'floating-rate', 'municipal', 'mbs', 'aggregate', 'international', 'emerging-markets', 'other'],
  crypto: ['layer-1', 'layer-2', 'layer-0', 'stablecoin', 'defi', 'exchange-token', 'meme', 'meme-coin', 'infrastructure', 'payment-token', 'payments', 'oracle', 'enterprise', 'cross-pair', 'privacy', 'ai-token', 'gaming', 'other'],
  funds: ['large-cap-blend', 'large-cap-growth', 'large-cap-value', 'mid-cap-blend', 'small-cap-blend', 'total-market', 'dividend', 'dividend-growth', 'sector', 'developed-ex-us', 'developed-markets', 'broad-market', 'total-ex-us', 'all-world', 'em-large-mid', 'em-all-cap', 'minimum-volatility', 'momentum', 'quality', 'factor', 'value', 'size', 'regional-equities', 'china', 'japan', 'europe', 'mexico', 'brazil', 'south-africa', 'frontier-blend', 'em-thematic', 'other']
} as const satisfies Readonly<Record<AssetClassType, readonly SubAssetClass[]>>;

/**
 * Valid sub-asset classes mapped by asset class AND market segment
 * Provides per-segment filtering for sub-asset class selection UI
 *
 * Each asset class entry must include exactly the market segments defined in
 * VALID_MARKET_SEGMENTS_BY_ASSET_CLASS — enforced by the mapped satisfies type.
 * The "other" market segment for each asset class includes the full set of
 * sub-asset classes (identical to VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS).
 */
export const VALID_SUB_ASSET_CLASSES_BY_ASSET_AND_SEGMENT = {
  // Futures: all segments share the same contract-size sub-asset classes
  futures: {
    indices: ['nano', 'micro', 'mini', 'standard', 'other'],
    commodities: ['nano', 'micro', 'mini', 'standard', 'other'],
    metals: ['nano', 'micro', 'mini', 'standard', 'other'],
    currencies: ['nano', 'micro', 'mini', 'standard', 'other'],
    financials: ['nano', 'micro', 'mini', 'standard', 'other'],
    energies: ['nano', 'micro', 'mini', 'standard', 'other'],
    meats: ['nano', 'micro', 'mini', 'standard', 'other'],
    softs: ['nano', 'micro', 'mini', 'standard', 'other'],
    other: ['nano', 'micro', 'mini', 'standard', 'other'],
  },

  // CFD: sub-asset classes vary by market segment
  cfd: {
    indices: ['stock_index', 'stock-index', 'other'],
    commodities: ['energy', 'crude-oil', 'natural-gas', 'other'],
    metals: ['precious', 'industrial', 'other'],
    'us-equities': ['single_stock', 'other'],
    'eu-equities': ['single_stock', 'other'],
    other: ['stock_index', 'stock-index', 'single_stock', 'energy', 'crude-oil', 'natural-gas', 'precious', 'industrial', 'other'],
  },

  // Forex: all segments share the same currency-category sub-asset classes
  forex: {
    currencies: ['g10', 'em', 'other'],
    other: ['g10', 'em', 'other'],
  },

  // Options: sub-asset classes vary by market segment
  options: {
    indices: ['index-options', 'futures-options', 'other'],
    'us-equities': ['equity-options', 'etf-options', 'other'],
    metals: ['futures-options', 'other'],
    energies: ['futures-options', 'other'],
    commodities: ['futures-options', 'other'],
    currencies: ['futures-options', 'other'],
    rates: ['futures-options', 'other'],
    other: ['equity-options', 'etf-options', 'index-options', 'futures-options', 'other'],
  },

  // Stocks: sub-asset classes vary by market segment
  stocks: {
    'us-equities': ['stock', 'etf', 'adr', 'other'],
    'eu-equities': ['stock', 'etf', 'other'],
    'emerging-markets': ['stock', 'etf', 'adr', 'other'],
    other: ['stock', 'etf', 'adr', 'other'],
  },

  // Bonds: sub-asset classes vary by market segment
  bonds: {
    government: ['treasury', 'tips', 'other'],
    corporate: ['investment-grade', 'high-yield', 'floating-rate', 'other'],
    municipal: ['municipal', 'general-obligation', 'other'],
    securitized: ['mortgage-backed', 'mbs', 'other'],
    aggregate: ['broad-market', 'aggregate', 'other'],
    international: ['developed-markets', 'emerging-markets', 'international', 'other'],
    other: ['treasury', 'tips', 'investment-grade', 'high-yield', 'general-obligation', 'mortgage-backed', 'broad-market', 'developed-markets', 'floating-rate', 'municipal', 'mbs', 'aggregate', 'international', 'emerging-markets', 'other'],
  },

  // Crypto: all segments share the same token-type sub-asset classes
  crypto: {
    'digital-assets': ['layer-1', 'layer-2', 'layer-0', 'stablecoin', 'defi', 'exchange-token', 'meme', 'meme-coin', 'infrastructure', 'payment-token', 'payments', 'oracle', 'enterprise', 'cross-pair', 'privacy', 'ai-token', 'gaming', 'other'],
    other: ['layer-1', 'layer-2', 'layer-0', 'stablecoin', 'defi', 'exchange-token', 'meme', 'meme-coin', 'infrastructure', 'payment-token', 'payments', 'oracle', 'enterprise', 'cross-pair', 'privacy', 'ai-token', 'gaming', 'other'],
  },

  // Funds: sub-asset classes vary by market segment
  funds: {
    'us-equities': ['large-cap-blend', 'large-cap-growth', 'large-cap-value', 'mid-cap-blend', 'small-cap-blend', 'total-market', 'dividend', 'dividend-growth', 'sector', 'minimum-volatility', 'momentum', 'quality', 'factor', 'value', 'size', 'other'],
    'eu-equities': ['regional-equities', 'europe', 'other'],
    'emerging-markets': ['em-large-mid', 'em-all-cap', 'em-thematic', 'other'],
    'international-equities': ['developed-ex-us', 'total-ex-us', 'other'],
    'global-equities': ['all-world', 'developed-markets', 'broad-market', 'other'],
    'asia-ex-japan': ['regional-equities', 'other'],
    asia: ['regional-equities', 'japan', 'china', 'other'],
    'latin-america': ['regional-equities', 'mexico', 'brazil', 'other'],
    'frontier-markets': ['frontier-blend', 'other'],
    other: ['large-cap-blend', 'large-cap-growth', 'large-cap-value', 'mid-cap-blend', 'small-cap-blend', 'total-market', 'dividend', 'dividend-growth', 'sector', 'developed-ex-us', 'developed-markets', 'broad-market', 'total-ex-us', 'all-world', 'em-large-mid', 'em-all-cap', 'minimum-volatility', 'momentum', 'quality', 'factor', 'value', 'size', 'regional-equities', 'china', 'japan', 'europe', 'mexico', 'brazil', 'south-africa', 'frontier-blend', 'em-thematic', 'other'],
  },
} as const satisfies Readonly<{
  [K in AssetClassType]: {
    [S in (typeof VALID_MARKET_SEGMENTS_BY_ASSET_CLASS)[K][number]]: readonly SubAssetClass[];
  };
}>;

// Compile-time completeness checks - ensure no union members are missing
// If a union or array is changed, this type will error to flag a mismatch
type _AllAssetClassTypesCovered = Exclude<AssetClassType, (typeof ASSET_CLASS_TYPES)[number]> extends never ? true : never;
type _AllMarketSegmentsCovered = Exclude<MarketSegment, (typeof MARKET_SEGMENTS)[number]> extends never ? true : never;
type _AllSubAssetClassTypesCovered = Exclude<SubAssetClass, (typeof SUB_ASSET_CLASS_TYPES)[number]> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage checks only - bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _AssetClassTypeChecks = [
  _AllAssetClassTypesCovered,
  _AllMarketSegmentsCovered,
  _AllSubAssetClassTypesCovered
];

// TODO(Phase 3): add bound validator wrappers once parametric utils exist
